import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (sid, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sid}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentDetails(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (_error) {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }
    pollPaymentStatus(sessionId, 0);
  }, [sessionId, navigate, pollPaymentStatus]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#FDFDFC' }}>
      <div className="max-w-2xl w-full mx-auto p-8">
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto mb-6 animate-spin" style={{ color: '#0284C7' }} />
              <h2 className="text-3xl font-light mb-4" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>Confirming Your Payment...</h2>
              <p className="text-lg" style={{ color: '#475569' }}>Please wait while we verify your booking</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 mx-auto mb-6" style={{ color: '#10B981' }} />
              <h2 className="text-3xl font-light mb-4" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>Booking Confirmed!</h2>
              <p className="text-lg mb-8" style={{ color: '#475569' }}>
                Thank you for choosing Radius Cleaning Company. Your booking has been confirmed and you will receive a confirmation email shortly.
              </p>
              <button
                data-testid="back-home-btn"
                onClick={() => navigate('/')}
                className="px-8 py-3 rounded-full font-medium"
                style={{ background: '#0284C7', color: '#FFFFFF' }}
              >
                Back to Home
              </button>
            </>
          )}

          {(status === 'error' || status === 'timeout' || status === 'expired') && (
            <>
              <XCircle className="w-16 h-16 mx-auto mb-6" style={{ color: '#EF4444' }} />
              <h2 className="text-3xl font-light mb-4" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>Payment Issue</h2>
              <p className="text-lg mb-8" style={{ color: '#475569' }}>
                {status === 'expired' ? 'Your payment session has expired.' : 'There was an issue processing your payment.'} Please try again or contact us for assistance.
              </p>
              <button
                data-testid="try-again-btn"
                onClick={() => navigate('/')}
                className="px-8 py-3 rounded-full font-medium"
                style={{ background: '#0284C7', color: '#FFFFFF' }}
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingSuccess;